import { useNewCode } from '@calendly/feature-check-react';
import { useFormField } from '@calendly/form';
import { ControlledRichTextEditor } from '@calendly/rich-text-editor';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useState, useCallback, useRef, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useGetUserQuery } from 'common';
import { useSimpleLinkPopup } from 'components/core/insert_link_popup';
import { BoxWithLinkTooltip, useSimpleLinkTooltip } from 'components/core/link_tooltip';
import { SuggestTimes } from 'components/suggest_times';
import { useTrack } from 'hooks/analytics_events';
import { insertHtmlAtCursor, getQuillEditor } from 'shared/utils';
import { trackToggleRichTextEditorToolbar } from '../analytics';
import { InsertLinkPopup } from '../insert_link_popup';
import { eventTypeSelector, sourceSelector } from '../selectors';
const bodyWrapperCss = "bie6zwj";
const BodyWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bodyWrapperCss, props.className)} />;
});
const bodyWrapperWithNegativeMarginCss = "blffg5g";
const BodyWrapperWithNegativeMargin = forwardRef((props, ref) => <BodyWrapper ref={ref} {...props} className={classNames(bodyWrapperWithNegativeMarginCss, props.className)} />);
const bodyWrapperWithNoMarginCss = "bryzrdz";
const BodyWrapperWithNoMargin = forwardRef((props, ref) => <BodyWrapper ref={ref} {...props} className={classNames(bodyWrapperWithNoMarginCss, props.className)} />);
export const BodyField = ({
  hideToolbar = null,
  hideSuggestTimes = false
}) => {
  const [isToggleToolbar, setToolbarToggle] = useState(false);
  const isSendFromCalendlyAtteEnabled = useNewCode('send_from_calendly_atte')?.enabled;
  const [fieldProps, meta] = useFormField({
    name: 'body'
  });
  const [{
    value: toValue
  }] = useFormField({
    name: 'to'
  });
  const eventType = useSelector(eventTypeSelector);
  const source = useSelector(sourceSelector);
  const {
    data: userData
  } = useGetUserQuery();
  const error = meta.touched && meta.error;
  const isPhone = useDeviceType('isPhone');
  const editorRef = useRef(null);
  const track = useTrack();
  const {
    id: eventTypeId
  } = eventType ?? {};
  const {
    openLinkPopup,
    linkPopupProps
  } = useSimpleLinkPopup();
  const {
    editorProps,
    tooltipProps
  } = useSimpleLinkTooltip({
    onLinkEdit: openLinkPopup,
    useNarrowTooltip: true
  });
  const contactIdentifier = toValue?.[0]?.uuid ?? toValue?.[0]?.id;
  const contactTimeZone = toValue?.[0]?.timeZone;
  const handleEditorToolbarToggle = useCallback(() => {
    setToolbarToggle(currentState => {
      const isToggled = !currentState;
      trackToggleRichTextEditorToolbar({
        track,
        isShown: isToggled,
        eventTypeId,
        contactIdentifier,
        source
      });
      return isToggled;
    });
  }, [eventTypeId, contactIdentifier, source, track]);
  const WrapperComponent = hideToolbar ? BodyWrapperWithNoMargin : BodyWrapperWithNegativeMargin;
  const handleAvailabilitySelect = data => {
    if (data?.generatedContent) {
      const {
        html: htmlContent
      } = data.generatedContent;
      const quillEditor = getQuillEditor(editorRef);
      if (quillEditor) {
        insertHtmlAtCursor(quillEditor, htmlContent);
      }
    }
  };
  return <WrapperComponent>
      <BoxWithLinkTooltip useNarrowTooltip {...tooltipProps}>
        <ControlledRichTextEditor {...fieldProps} ref={editorRef} onLinkEdit={openLinkPopup} {...editorProps} hasError={Boolean(error)} noTopRounding noBottomRounding={isPhone} toggleToolbar={isToggleToolbar} hideToolbar={hideToolbar} />
        {isSendFromCalendlyAtteEnabled && !hideSuggestTimes && <SuggestTimes eventType={eventType} timeNotation={userData?.time_notation} timezone={contactTimeZone || eventType?.availability_time_zone} onAvailabilitySelect={handleAvailabilitySelect} handleEditorToolbarToggle={handleEditorToolbarToggle} toggleToolbar={isToggleToolbar} contact={contactIdentifier ? {
        uuid: contactIdentifier,
        timeZone: contactTimeZone
      } : undefined} source={source} isSendFromCalendly />}
      </BoxWithLinkTooltip>
      <InsertLinkPopup {...linkPopupProps} prefilledURL={eventType?.booking_url || eventType?.bookingUrl} />
    </WrapperComponent>;
};

require("./body_field.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./body_field.js");